import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV4,
  ButtonV2,
  LabraToolTipV2,
  PlanType,
  TextInputV4,
} from '@labrav/react-components'

import { FieldArray, FormikProps, useFormikContext } from 'formik'
import { get } from 'lodash'

import { isNonEmptyNumber } from '@labrav/common'
import clsx from 'clsx'

import { useStyles } from './PricingSaasSubscriptions.styles'
import {
  CombinedFormDimension,
  CombinedFormState,
} from '../../../../modules/combinedForm/reducer'
import { Typography } from '@mui/material'
import { ShortenedNameV2 } from '@labrav/common'
import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'
import { HelpDrawer } from '@labrav/common'

export const PricingSaasSubscriptions = ({
  disabled,
}: {
  productType?: PlanType
  disabled: boolean
}) => {
  const { classes } = useStyles()
  const formikProps = useFormikContext() as FormikProps<CombinedFormState>
  const { touched, errors, values, handleBlur, setFieldValue } = formikProps
  const dimensions = (values.resellerAgreementData.dimensions ||
    []) as CombinedFormDimension[]
  const currencySymbol =
    getCurrencySymbol(values?.resellerAgreementData.currency as string) || '$'

  const getErrorMsg = (keyName: string) => {
    return ((!!get(touched, keyName, false) && get(errors, keyName, ' ')) ||
      '') as string
  }

  const applyAdjustmentPercentage = async () => {
    setFieldValue(
      'cppoData.adjustmentPercentage',
      values?.cppoData?.adjustmentPercentageInputValue
    )
  }

  const handleChangeMarkupPercentageValue = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value
    await setFieldValue(
      'cppoData.adjustmentPercentageInputValue',
      value === '' ? null : value
    )
  }
  const isApplied =
    isNonEmptyNumber(
      values?.cppoData.adjustmentPercentageInputValue as number
    ) &&
    values?.cppoData.adjustmentPercentageInputValue ===
      values?.cppoData.adjustmentPercentage

  const applyDisable =
    !isNonEmptyNumber(
      values?.cppoData.adjustmentPercentageInputValue as number
    ) || !!get(errors, 'cppoData.adjustmentPercentageInputValue', false)

  const isErrorForValue = !!(
    get(touched, 'cppoData.adjustmentPercentageInputValue', false) &&
    get(errors, 'cppoData.adjustmentPercentageInputValue', false)
  )
  const getBuyerPrice = (isvPrice: number) => {
    const percentage = Number(values?.cppoData.adjustmentPercentage || 0)
    if (!isvPrice || isNaN(isvPrice)) return 0
    return parseFloat((isvPrice * (1 + percentage / 100)).toFixed(2))
  }

  return (
    <div data-testid={'markup-section'}>
      <BoxV4
        heading={'Pricing'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
        secondaryActions={
          <HelpDrawer
            sectionName="combinedFormPricingSubscription"
            headerTitle="Help & FAQs"
          />
        }
      >
        <div className={classes.markupWrapper}>
          <div className={classes.description}>
            Enter the markup % to calculate the buyer price based on the
            discounted prices entered above. The same markup is applied across
            all usage dimensions.
          </div>
          <div className={classes.inputWrapper}>
            <div className={classes.input}>
              <TextInputV4
                keyName="cppoData.adjustmentPercentageInputValue"
                title={'Markup percentage'}
                type="text"
                placeHolder=""
                value={
                  isNonEmptyNumber(
                    values?.cppoData.adjustmentPercentageInputValue as number
                  )
                    ? values.cppoData.adjustmentPercentageInputValue!
                    : ''
                }
                updatedValue={
                  isNonEmptyNumber(
                    values?.cppoData.adjustmentPercentageInputValue as number
                  )
                    ? values.cppoData.adjustmentPercentageInputValue!
                    : ''
                }
                isError={
                  !!(
                    get(
                      touched,
                      'cppoData.adjustmentPercentageInputValue',
                      false
                    ) &&
                    get(
                      errors,
                      'cppoData.adjustmentPercentageInputValue',
                      false
                    )
                  )
                }
                onChange={handleChangeMarkupPercentageValue}
                isReadOnly={disabled}
                isRequired={false}
                errorText={getErrorMsg(
                  'cppoData.adjustmentPercentageInputValue'
                )}
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={
                applyDisable
                  ? get(
                      errors,
                      'cppoData.adjustmentPercentageInputValue',
                      'Please enter number.'
                    )
                  : ''
              }
            >
              <div>
                <ButtonV2
                  className={clsx(
                    classes.applyButton,
                    isErrorForValue ? classes.disabledApply : ''
                  )}
                  smallVariant
                  styleType="outlined"
                  onClick={applyAdjustmentPercentage}
                  disabled={applyDisable || disabled}
                >
                  {isApplied ? 'Applied' : 'Apply'}
                </ButtonV2>
                {!isApplied &&
                  formikProps?.submitCount > 0 &&
                  !applyDisable && (
                    <div className={classes.errorMessage}>
                      Please apply the markup.
                    </div>
                  )}
              </div>
            </LabraToolTipV2>
          </div>
        </div>
        {/* dims */}
        <div className={clsx(classes.root, classes.elementsGap)}>
          <div className={classes.titleWraper}>
            <Typography
              className={clsx(
                classes.sectionText,
                classes.columnName,
                classes.fontSize
              )}
            >
              Plan
            </Typography>
            <Typography
              className={clsx(
                classes.sectionText,
                classes.columnName,
                classes.fontSize
              )}
              style={{ textAlign: 'right' }}
            >
              Price from buyer
            </Typography>
          </div>
          <div className={classes.border}></div>
          <FieldArray name="resellerAgreementData.dimensions">
            {() => (
              <>
                {dimensions.map((dimension, index) => {
                  const dimensionKey = dimension.id || index
                  const buyerPrice = getBuyerPrice(
                    dimension?.prices?.subscriptionPrice as number
                  )

                  return (
                    <>
                      <div
                        className={classes.resourceRow}
                        data-testid="dimensions-row"
                      >
                        <div className={classes.resourcesContainer}>
                          <div
                            className={clsx(
                              classes.columnName,
                              classes.fontSizeDesc
                            )}
                            key={`resellerAgreementData.dimensions-${
                              dimension.id || index
                            }-paymentDate`}
                          >
                            <div
                              className={clsx(
                                classes.descHeightWidth,
                                classes.textStyle
                              )}
                              key={`resellerAgreementData.dimensions-${dimensionKey}-name`}
                              data-testid="dimension-name"
                            >
                              <ShortenedNameV2
                                name={dimensions[index].name || '-'}
                                length={50}
                              />
                            </div>
                          </div>
                          <div
                            className={clsx(classes.descHeightWidth)}
                            key={`resellerAgreementData.dimensions-${dimensionKey}-desc`}
                            data-testid="description-select"
                          >
                            <Typography className={classes.alignTypography}>
                              <Typography
                                style={{ display: 'flex', fontSize: '14px' }}
                              >
                                <span className={classes.currencyClass}>
                                  {currencySymbol}
                                </span>
                                {(() => {
                                  const price = buyerPrice || 0
                                  return Number.isInteger(price)
                                    ? price
                                    : Number(price.toFixed(2))
                                })()}
                              </Typography>
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </>
                  )
                })}
              </>
            )}
          </FieldArray>
        </div>
      </BoxV4>
    </div>
  )
}
